/**
 * Dismiss the native launch splash on Capacitor (iOS + Android).
 *
 * The web app is wrapped in a thin Capacitor WKWebView (iOS) / WebView
 * (Android) shell that shows a native splash image on launch. By default that
 * splash auto-hides on a fixed timer (`launchShowDuration`) that isn't aware of
 * when the web content is actually ready — which is what produced the visible
 * gap between the splash fading and the logo animating in. The reliable pattern
 * (per Capacitor's own guidance) is to set `launchAutoHide: false` in the native
 * config and have the web app call `SplashScreen.hide()` the moment its first
 * screen is painted, so the splash fade and the app's entrance are in sync.
 *
 * This helper is:
 * - **cross-platform** — it keys off `Capacitor.isNativePlatform()`, so it works
 *   on both iOS and Android and is a no-op in a plain web browser / PWA (where
 *   there is no native splash to hide);
 * - **idempotent** — safe to call from multiple entry screens; the underlying
 *   plugin call is a no-op once the splash is already gone.
 *
 * `fadeOutMs` maps to the iOS `hide({ fadeOutDuration })`. On Android the
 * fade-out is driven by the native `launchFadeOutDuration` config instead, but
 * the call still dismisses the splash.
 */

interface CapacitorSplashScreenPlugin {
  hide?: (options?: { fadeOutDuration?: number }) => Promise<void>;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  Plugins?: { SplashScreen?: CapacitorSplashScreenPlugin };
}

let hidden = false;

export function hideNativeSplash(fadeOutMs = 300): void {
  if (hidden) return;

  const capacitor = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  if (!capacitor?.isNativePlatform?.()) {
    // Plain web browser or installed PWA: the browser/OS owns the splash and
    // dismisses it on first paint, so there's nothing for us to hide.
    return;
  }

  hidden = true;
  capacitor.Plugins?.SplashScreen?.hide?.({ fadeOutDuration: fadeOutMs })?.catch((e) => {
    console.error("SplashScreen.hide failed", e);
  });
}
